import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, Mail, Phone, User, Calendar, MessageSquare } from "lucide-react";
import { useState } from "react";
import { usePendingMemberships, PendingMembership } from "@/hooks/usePendingMemberships";
import { formatDistanceToNow } from "date-fns";

export const PendingMembershipsManager = () => {
  const {
    pendingMemberships,
    loading,
    processing,
    approveMembership,
    rejectMembership,
    bulkApprove,
    bulkReject,
  } = usePendingMemberships();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionNotes, setActionNotes] = useState('');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');

  const pendingOnly = pendingMemberships.filter(m => m.status === 'pending');

  const handleSelectAll = () => {
    if (selectedIds.length === pendingOnly.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingOnly.map(m => m.id));
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkAction = async () => {
    if (selectedIds.length === 0) return;

    try {
      if (actionType === 'approve') {
        await bulkApprove(selectedIds);
      } else {
        await bulkReject(selectedIds, actionNotes);
      }
      
      setSelectedIds([]);
      setActionNotes('');
      setActionDialogOpen(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const handleSingleAction = async (membership: PendingMembership, action: 'approve' | 'reject', notes?: string) => {
    try {
      if (action === 'approve') {
        await approveMembership(membership.id, notes);
      } else {
        await rejectMembership(membership.id, notes);
      }
    } catch (error) {
      console.error('Single action failed:', error);
    }
  };

  if (loading) {
    return <div>Loading pending memberships...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Pending Membership Requests</span>
          <Badge variant="secondary">{pendingOnly.length} pending</Badge>
        </CardTitle>
        <CardDescription>
          Review and manage membership requests that require approval
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingOnly.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending membership requests</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bulk Actions */}
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <span className="text-sm font-medium">
                  {selectedIds.length} selected
                </span>
                <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActionType('approve')}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Bulk Approve
                    </Button>
                  </DialogTrigger>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActionType('reject')}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Bulk Reject
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {actionType === 'approve' ? 'Approve' : 'Reject'} {selectedIds.length} Memberships
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notes (optional)</Label>
                        <Textarea
                          id="notes"
                          placeholder={`Add a note about this ${actionType} action...`}
                          value={actionNotes}
                          onChange={(e) => setActionNotes(e.target.value)}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setActionDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkAction}
                          disabled={processing}
                          variant={actionType === 'approve' ? 'default' : 'destructive'}
                        >
                          {processing ? 'Processing...' : `${actionType === 'approve' ? 'Approve' : 'Reject'} All`}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Memberships Table */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === pendingOnly.length && pendingOnly.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingOnly.map((membership) => (
                    <TableRow key={membership.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(membership.id)}
                          onCheckedChange={() => handleSelectOne(membership.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {membership.user_profile?.name || 'Unknown User'}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {membership.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          {membership.user_profile?.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {membership.user_profile.email}
                            </div>
                          )}
                          {membership.user_profile?.phone_number && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {membership.user_profile.phone_number}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDistanceToNow(new Date(membership.requested_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline">
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Approve Membership</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>User: {membership.user_profile?.name}</Label>
                                  <Label>Email: {membership.user_profile?.email}</Label>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="approve-notes">Notes (optional)</Label>
                                  <Textarea
                                    id="approve-notes"
                                    placeholder="Add a note about this approval..."
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline">Cancel</Button>
                                  <Button
                                    onClick={() => handleSingleAction(membership, 'approve')}
                                    disabled={processing}
                                  >
                                    Approve
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline">
                                <X className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Reject Membership</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>User: {membership.user_profile?.name}</Label>
                                  <Label>Email: {membership.user_profile?.email}</Label>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="reject-notes">Reason for rejection</Label>
                                  <Textarea
                                    id="reject-notes"
                                    placeholder="Explain why this membership is being rejected..."
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline">Cancel</Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleSingleAction(membership, 'reject')}
                                    disabled={processing}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};